/**
 * Token Service
 * Handles secure token storage and refresh logic
 *
 * Security:
 * - Access tokens stored in memory only (not localStorage)
 * - Refresh tokens stored as httpOnly cookies (managed by backend)
 * - Automatic refresh before token expiration
 *
 * Identity:
 * - The backend embeds the authenticated user's profile in the access token
 *   AND returns it inline on /auth/token & /auth/refresh, so we cache it here
 *   and skip the legacy /api/getUserInfo round-trip entirely.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import { getBaseURL } from '../constants/common.constants';
const baseURL = getBaseURL();
// Store access token in memory (not localStorage!)
let accessToken = null;
let tokenExpiresAt = 0;
let refreshPromise = null;
// Cached user profile derived from the latest token response.
let cachedUser = null;
/**
 * Non-sensitive marker written to localStorage when we know a refresh-token
 * cookie likely exists. Used by AuthContext to decide whether to attempt a
 * silent /auth/refresh on app load, or skip straight to the MSAL exchange.
 *
 * SECURITY NOTE: this is purely a hint, NOT a credential. An attacker setting
 * it to '1' just causes a single 401 from /auth/refresh, after which we fall
 * through to the MSAL exchange anyway. Removing it just means one extra
 * /auth/refresh call on the next page load — no security impact.
 *
 * localStorage (not sessionStorage) is intentional: sessionStorage is cleared
 * on hard refresh, which caused the fast cookie-based restore path to be
 * skipped and the slow MSAL acquireTokenSilent path to be taken every time.
 */
const SESSION_MARKER_KEY = 'hasJwtSession';
function markSessionActive() {
    try {
        localStorage.setItem(SESSION_MARKER_KEY, '1');
    }
    catch (_a) {
        /* localStorage unavailable (private mode etc.) — ignore */
    }
}
function clearSessionMarker() {
    try {
        localStorage.removeItem(SESSION_MARKER_KEY);
    }
    catch (_a) {
        /* ignore */
    }
}
// Create axios instance for auth requests (separate from main instance)
const authAxios = axios.create({
    baseURL,
    withCredentials: true, // Important for cookies
});
export const tokenService = {
    /**
     * Exchange MSAL token for JWT
     * Called after successful Azure AD SSO login
     */
    exchangeToken(msalToken) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield authAxios.post('/auth/token', { msal_token: msalToken });
            this.setAccessToken(response.data.access_token, response.data.expires_in);
            if (response.data.user) {
                cachedUser = response.data.user;
            }
            markSessionActive();
            return response.data;
        });
    },
    /**
     * Store access token in memory
     * Sets expiry 30 seconds before actual expiry for buffer
     */
    setAccessToken(token, expiresIn) {
        accessToken = token;
        // Set expiry 30 seconds before actual expiry to allow buffer for refresh
        tokenExpiresAt = Date.now() + (expiresIn - 30) * 1000;
    },
    /**
     * Get current access token, refreshing if needed
     * Use this before making API calls
     */
    getAccessToken() {
        return __awaiter(this, void 0, void 0, function* () {
            // If token exists and not expired, return it
            if (accessToken && Date.now() < tokenExpiresAt) {
                return accessToken;
            }
            // Delegate to refreshToken — it handles in-flight de-duplication
            // so concurrent callers share a single /auth/refresh round-trip.
            return this.refreshToken();
        });
    },
    /**
     * Refresh access token using refresh token cookie.
     *
     * IMPORTANT: refresh tokens are single-use (the backend rotates them on
     * every successful refresh). If two callers fire /auth/refresh in
     * parallel, the second will 401 and tear down the session. This method
     * therefore de-duplicates concurrent calls — every caller (checkSession,
     * getCurrentUser, getAccessToken, axios 401 retry) awaits the same
     * promise until it settles.
     */
    refreshToken() {
        return __awaiter(this, void 0, void 0, function* () {
            if (refreshPromise) {
                return refreshPromise;
            }
            refreshPromise = (() => __awaiter(this, void 0, void 0, function* () {
                try {
                    const response = yield authAxios.post('/auth/refresh');
                    this.setAccessToken(response.data.access_token, response.data.expires_in);
                    if (response.data.user) {
                        cachedUser = response.data.user;
                    }
                    markSessionActive();
                    return response.data.access_token;
                }
                catch (error) {
                    console.error('Token refresh failed:', error);
                    this.clearToken();
                    return null;
                }
                finally {
                    refreshPromise = null;
                }
            }))();
            return refreshPromise;
        });
    },
    /**
     * Clear access token from memory and the session-restore hint.
     */
    clearToken() {
        accessToken = null;
        tokenExpiresAt = 0;
        cachedUser = null;
        clearSessionMarker();
    },
    /**
     * Logout - revoke refresh token on server
     */
    logout() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield authAxios.post('/auth/logout');
            }
            catch (error) {
                console.error('Logout request failed:', error);
            }
            finally {
                this.clearToken();
            }
        });
    },
    /**
     * Check if we have a potentially valid session
     * Note: actual validation happens on API call
     */
    hasToken() {
        return accessToken !== null && Date.now() < tokenExpiresAt;
    },
    /**
     * Cheap synchronous hint that a refresh-token cookie *probably* exists for
     * this browser session. Set when /auth/token or /auth/refresh succeeded;
     * cleared on logout or refresh failure.
     *
     * Use this BEFORE issuing /auth/refresh on app load to avoid a guaranteed
     * 401 round-trip when the user has never logged in this session.
     */
    hasSessionHint() {
        try {
            return localStorage.getItem(SESSION_MARKER_KEY) === '1';
        }
        catch (_a) {
            return false;
        }
    },
    /**
     * Synchronously read the cached user profile derived from the most recent
     * /auth/token or /auth/refresh response. Returns null until the first
     * successful exchange.
     */
    getCachedUser() {
        return cachedUser;
    },
    /**
     * Get current user info.
     *
     * Identity is now baked into every /auth/token and /auth/refresh response,
     * so we simply return the cached profile. If it's null, the caller should
     * trigger a refresh (which will repopulate the cache) instead of issuing a
     * separate request.
     */
    getCurrentUser() {
        return __awaiter(this, void 0, void 0, function* () {
            if (cachedUser)
                return cachedUser;
            // Cache miss: try a silent refresh, which will repopulate cachedUser.
            const token = yield this.refreshToken();
            return token ? cachedUser : null;
        });
    },
    /**
     * Check if refresh token exists (by attempting silent refresh)
     * Used on app load to check for existing session
     */
    checkSession() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const token = yield this.refreshToken();
                return token !== null;
            }
            catch (_a) {
                return false;
            }
        });
    }
};
